import React, { useState, useEffect } from 'react';
import { Search, Eye, Download, Upload, FileText, Trash2, CheckCircle, XCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { studentsAPI, staffAPI, consultantsAPI, masterSettingsAPI, suppliersAPI } from '@/api/api';
import { readFileSmart } from '@/utils/imageCompressor';

const SuppliersDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [formData, setFormData] = useState({ entity_id: '', entity_name: '', title: '', document_type: 'general', file_name: '', file_data: '', file_type: '', file_size: 0, description: '' });

  useEffect(() => { fetchDocuments(); fetchSuppliers(); }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/transcripts/suppliers');
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersAPI.getSuppliers();
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error) { console.error('Error fetching suppliers:', error); }
  };

  const processSelectedFile = async (file, originalName, originalSize, mimeType) => {
    if (originalSize > 5 * 1024 * 1024) { alert('File size must be less than 5MB'); return; }
    try {
      const result = await readFileSmart(file);
      setFormData(prev => ({
        ...prev,
        file_name: originalName,
        file_data: result.data,
        file_type: result.type || (mimeType.split('/')[1] || 'pdf'),
        file_size: result.size
      }));
      if (result.compressed) {
        console.log(`Image compressed: ${(originalSize/1024).toFixed(0)}KB -> ${(result.size/1024).toFixed(0)}KB`);
      }
    } catch (err) {
      console.error('File read failed:', err);
      alert('Failed to read file. Please try again.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Snapshot primitives synchronously, then hand off to async processor
    processSelectedFile(file, file.name, file.size, file.type || 'application/octet-stream');
  };

  const handleSupplierSelect = (id) => {
    const supplier = suppliers.find(s => s.id === id);
    if (supplier) { setFormData({ ...formData, entity_id: supplier.id, entity_name: supplier.name || supplier.supplier_name }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_data) { alert('Please select a file'); return; }
    try {
      await api.post('/transcripts/documents', { ...formData, entity_type: 'supplier' });
      alert('Document uploaded!');
      setShowModal(false);
      setFormData({ entity_id: '', entity_name: '', title: '', document_type: 'general', file_name: '', file_data: '', file_type: '', file_size: 0, description: '' });
      fetchDocuments();
    } catch (error) { alert('Failed to upload'); }
  };

  const handleView = async (docId) => {
    try {
      const response = await api.get(`/transcripts/documents/${docId}`);
      setSelectedDoc(response.data);
      setShowViewModal(true);
    } catch (error) { alert('Failed to load'); }
  };

  const handleVerify = async (docId, status) => {
    try {
      await api.put(`/transcripts/documents/${docId}/verify?status=${status}`);
      fetchDocuments();
    } catch (error) { alert('Failed'); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try { await api.delete(`/transcripts/documents/${docId}`); fetchDocuments(); }
    catch (error) { alert('Failed'); }
  };

  const filteredDocuments = documents.filter(doc => doc.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) || doc.title?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Suppliers Documents</h1>
          <p className="text-sm text-gray-500 mt-1">Manage supplier document records</p>
        </div>
        <Button onClick={() => setShowModal(true)} className="flex items-center gap-2"><Upload className="h-4 w-4" />Upload Document</Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input type="text" placeholder="Search by name, title..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Document Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Upload Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (<tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>) : filteredDocuments.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-500"><FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />No documents found.</td></tr>
                ) : (filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium">{doc.entity_name}</td>
                    <td className="px-4 py-3 text-sm">{doc.title}</td>
                    <td className="px-4 py-3 text-sm capitalize">{doc.document_type}</td>
                    <td className="px-4 py-3 text-sm">{doc.created_at?.split('T')[0]}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full ${doc.status === 'Verified' ? 'bg-green-100 text-green-800' : doc.status === 'Rejected' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>{doc.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => handleView(doc.id)} className="text-blue-600 hover:text-blue-800"><Eye className="h-4 w-4" /></button>
                        {doc.status === 'Pending' && (<><button onClick={() => handleVerify(doc.id, 'Verified')} className="text-green-600"><CheckCircle className="h-4 w-4" /></button><button onClick={() => handleVerify(doc.id, 'Rejected')} className="text-orange-600"><XCircle className="h-4 w-4" /></button></>)}
                        <button onClick={() => handleDelete(doc.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-bold">Upload Document</h2><button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button></div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Select Supplier *</Label><Select value={formData.entity_id} onValueChange={handleSupplierSelect}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{suppliers.map(s => (<SelectItem key={s.id} value={s.id}>{s.name || s.supplier_name}</SelectItem>))}</SelectContent></Select></div>
              <div><Label>Title *</Label><Input required value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} /></div>
              <div><Label>File *</Label><Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} />{formData.file_name && <p className="text-sm text-green-600 mt-1">{formData.file_name}</p>}</div>
              <div className="flex gap-2"><Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button><Button type="submit" className="flex-1">Upload</Button></div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-bold">Document Details</h2><button onClick={() => setShowViewModal(false)}><X className="h-5 w-5" /></button></div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div><Label className="text-gray-500">Name</Label><p className="font-medium">{selectedDoc.entity_name}</p></div>
              <div><Label className="text-gray-500">Title</Label><p className="font-medium">{selectedDoc.title}</p></div>
            </div>
            {selectedDoc.file_data && (selectedDoc.file_type === 'pdf' ? <iframe src={selectedDoc.file_data} className="w-full h-96 border rounded" title="Doc" /> : <img src={selectedDoc.file_data} alt="Doc" className="max-w-full border rounded" />)}
            <div className="flex gap-2 pt-4">{selectedDoc.file_data && <a href={selectedDoc.file_data} download={selectedDoc.file_name} className="flex-1"><Button variant="outline" className="w-full"><Download className="h-4 w-4 mr-2" />Download</Button></a>}<Button onClick={() => setShowViewModal(false)} className="flex-1">Close</Button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersDocuments;